import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import type { Message } from '../../components/chat-page/App'
import { parseTimestamp } from '../../components/lib/dateUtils'
import { ChatMessages } from '../../components/chat-page-v2/components/ChatMessages'
import { InputBar } from '../../components/chat-page-v2/components/InputBar'
import { ConfirmationDialog } from '../../components/shared/ConfirmationDialog'
import { RenameDialog } from '../../components/chat-page/components/RenameDialog'
import { getCustomGPT, createCustomGPTChat, listCustomGPTChats, getCustomGPTChat, customGPTChatStream, editCustomGPTMessage } from '../../lib/api/customGPT'
import { API_BASE_URL } from '../../lib/config'
import type { CustomGPTResponse } from '../../types/custom-gpt'
import { useAuth } from '../../components/auth/AuthProvider'
import { CHAT_MODEL_CLAUDE_SONNET_45_SKILLS, DEFAULT_CHAT_MODEL_ID, isLlmConfigChatModelId } from '../../components/lib/llmConfigModels'
import { useAvailableChatModels } from '../../components/lib/useAvailableChatModels'

import '../../components/chat-page-v2/styles/chat-v2-bundle.css'
import './playbook-chat-v2.css'

type PlaybookChatSession = {
  id: string
  name: string
  createdAt: Date
  lastMessageAt: Date | null
  messageCount: number
  messages: Message[]
  messagesLoaded: boolean
  modelName?: string | null
}

function formatChatMeta(session: PlaybookChatSession): string {
  if (session.lastMessageAt) {
    return `Updated ${session.lastMessageAt.toLocaleDateString()}`
  }
  return `${session.messageCount} message${session.messageCount === 1 ? '' : 's'}`
}

const PLAYBOOK_CHAT_MODEL_STORAGE_KEY = 'aira-playbook-chat-model'

function sanitizeModel(v: string | null): string {
  return isLlmConfigChatModelId(v) ? v : DEFAULT_CHAT_MODEL_ID
}

export function PlaybookChatPageV2() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, credentialUser } = useAuth()

  const [playbook, setPlaybook] = useState<CustomGPTResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [isTyping, setIsTyping] = useState(false)
  const [chatModel, setChatModel] = useState<string>(() =>
    sanitizeModel(typeof window !== 'undefined' ? localStorage.getItem(PLAYBOOK_CHAT_MODEL_STORAGE_KEY) : null)
  )
  const { models: availableChatModels } = useAvailableChatModels()
  const playbookChatModelOptions = useMemo(
    () => availableChatModels.filter(m => m.id !== CHAT_MODEL_CLAUDE_SONNET_45_SKILLS),
    [availableChatModels]
  )
  const [sessions, setSessions] = useState<PlaybookChatSession[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [chatToDelete, setChatToDelete] = useState<string | null>(null)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [chatToRename, setChatToRename] = useState<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const creatingChatRef = useRef(false)
  const sessionsLoadedRef = useRef(false)

  const currentSession = sessions.find(s => s.id === currentSessionId) ?? null

  const messages = useMemo(() => {
    const msgs = currentSession?.messages ?? []
    return [...msgs].sort((a, b) => (a.timestamp?.getTime?.() ?? 0) - (b.timestamp?.getTime?.() ?? 0))
  }, [currentSession?.messages])

  const modelLocked = useMemo(() => messages.some(m => m.sender === 'user'), [messages])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    try {
      localStorage.setItem(PLAYBOOK_CHAT_MODEL_STORAGE_KEY, chatModel)
    } catch {
      void 0
    }
  }, [chatModel])

  useEffect(() => {
    if (!playbookChatModelOptions.length) return
    const ids = new Set(playbookChatModelOptions.map(m => m.id))
    if (ids.has(chatModel)) return
    setChatModel(playbookChatModelOptions[0].id)
  }, [playbookChatModelOptions, chatModel])

  useEffect(() => {
    if (!id) {
      navigate('/playbooks', { replace: true })
      return
    }

    setLoading(true)
    setPlaybook(null)
    sessionsLoadedRef.current = false
    creatingChatRef.current = false
    setSessions([])
    setCurrentSessionId(null)

    getCustomGPT(id)
      .then(data => setPlaybook(data))
      .catch(() => {
        toast.error('Failed to load playbook')
        navigate('/playbooks', { replace: true })
      })
      .finally(() => setLoading(false))
  }, [id, navigate])

  const loadSessionMessages = useCallback(async (sessionId: string) => {
    if (!id) return
    setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, messagesLoaded: true } : s)))
    try {
      const chatData = await getCustomGPTChat(id, sessionId)
      if (chatData.model_name && isLlmConfigChatModelId(chatData.model_name)) {
        setChatModel(chatData.model_name)
      }
      const msgs: Message[] = chatData.messages.map(m => ({
        id: m.id,
        content: m.content,
        sender: m.role === 'user' ? 'user' : 'aira',
        timestamp: parseTimestamp(m.timestamp),
        file_path: m.file_path,
        file_name: m.file_name,
        file_size: m.file_size,
      }))
      setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, modelName: chatData.model_name ?? s.modelName ?? null, messages: msgs, messagesLoaded: true } : s)))
    } catch (e) {
      console.error('Failed to load playbook chat messages', e)
      toast.error('Failed to load messages')
      setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, messagesLoaded: false } : s)))
    }
  }, [id])

  const createNewSession = useCallback(async () => {
    if (!id) return null
    if (creatingChatRef.current) return null
    creatingChatRef.current = true
    try {
      const chat = await createCustomGPTChat(id)
      const createdAt = parseTimestamp(chat.created_at)
      setSessions(prev => {
        const exists = prev.some(s => s.id === chat.id)
        if (exists) return prev
        return [{
          id: chat.id,
          name: chat.name,
          createdAt,
          lastMessageAt: null,
          messageCount: 0,
          modelName: null,
          messages: [],
          messagesLoaded: true,
        }, ...prev]
      })
      setCurrentSessionId(chat.id)
      return chat.id
    } catch (e) {
      console.error('Failed to create playbook chat', e)
      toast.error('Failed to create chat')
      return null
    } finally {
      creatingChatRef.current = false
    }
  }, [id])

  useEffect(() => {
    if (!id || !playbook) return
    if (sessionsLoadedRef.current) return
    sessionsLoadedRef.current = true

    listCustomGPTChats(id)
      .then(({ chats }) => {
        const normalized: PlaybookChatSession[] = chats.map(c => ({
          id: c.id,
          name: c.name,
          createdAt: parseTimestamp(c.created_at),
          lastMessageAt: c.last_message_at ? parseTimestamp(c.last_message_at) : null,
          messageCount: c.message_count,
          modelName: (c as unknown as { model_name?: string | null }).model_name ?? null,
          messages: [],
          messagesLoaded: false,
        }))
        setSessions(normalized)
        if (normalized.length > 0) {
          setCurrentSessionId(normalized[0].id)
          if (normalized[0].modelName && isLlmConfigChatModelId(normalized[0].modelName)) {
            setChatModel(normalized[0].modelName)
          }
          void loadSessionMessages(normalized[0].id)
        }
      })
      .catch(e => {
        console.error('Failed to list playbook chats', e)
      })
  }, [id, playbook, loadSessionMessages])

  const selectSession = (sessionId: string) => {
    setCurrentSessionId(sessionId)
    const s = sessions.find(x => x.id === sessionId)
    if (s?.modelName && isLlmConfigChatModelId(s.modelName)) {
      setChatModel(s.modelName)
    }
    if (s && !s.messagesLoaded) {
      void loadSessionMessages(sessionId)
    }
  }

  const stopStreaming = useCallback(() => {
    try {
      abortControllerRef.current?.abort()
    } catch {
      void 0
    }
    setIsTyping(false)
  }, [])

  const startNewChatDraft = useCallback(() => {
    stopStreaming()
    setCurrentSessionId(null)
  }, [stopStreaming])

  const takeNextStreamFrames = (buffer: string): { frames: string[]; rest: string } => {
    // Prefer the app-wide delimiter used by normal chat.
    const primaryDelim = '--|\n\n'
    if (buffer.includes(primaryDelim)) {
      const parts = buffer.split(primaryDelim)
      const rest = parts.pop() ?? ''
      return { frames: parts, rest }
    }
    // Fallback to standard SSE event boundary.
    const fallbackDelim = '\n\n'
    if (buffer.includes(fallbackDelim)) {
      const parts = buffer.split(fallbackDelim)
      const rest = parts.pop() ?? ''
      return { frames: parts, rest }
    }
    return { frames: [], rest: buffer }
  }

  const applyStreamFrame = (frame: string): { kind: 'text'; text: string } | { kind: 'ignore' } | { kind: 'error'; error: string } => {
    if (!frame.startsWith('data: ')) return { kind: 'ignore' }
    const piece = frame.slice(6)
    if (!piece) return { kind: 'ignore' }
    const trimmed = piece.trim()
    if (!trimmed || trimmed === '[DONE]') return { kind: 'ignore' }

    // Never append control payloads to markdown output.
    if (trimmed.startsWith('{')) {
      try {
        const obj = JSON.parse(trimmed) as { error?: string }
        if (typeof obj.error === 'string' && obj.error.length > 0) {
          return { kind: 'error', error: obj.error }
        }
        return { kind: 'ignore' }
      } catch {
        // Not valid JSON; treat as plain text delta.
      }
    }

    return { kind: 'text', text: piece }
  }

  const sendMessage = useCallback(async (content: string, file?: File) => {
    if (!id) return
    const sessionId = currentSessionId ?? await createNewSession()
    if (!sessionId) return

    const userMsg: Message = {
      id: Date.now().toString(),
      content,
      sender: 'user',
      timestamp: new Date(),
    }
    setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, messages: [...s.messages, userMsg], messageCount: s.messageCount + 1, lastMessageAt: new Date(), messagesLoaded: true } : s)))

    setIsTyping(true)
    abortControllerRef.current = new AbortController()
    // Show typing indicator immediately (before first stream chunk).
    const streamingPlaceholder: Message = { id: 'streaming', content: '', sender: 'aira', timestamp: new Date(), isStreaming: true }
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s
      const without = s.messages.filter(m => m.id !== 'streaming')
      return { ...s, messages: [...without, streamingPlaceholder], lastMessageAt: new Date(), messagesLoaded: true }
    }))
    // Persist selected model locally for this session (backend will lock it if not set already).
    setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, modelName: s.modelName ?? chatModel } : s)))

    try {
      const session = sessions.find(s => s.id === sessionId)
      const history = (session?.messages ?? []).map(m => ({
        role: m.sender === 'user' ? 'user' as const : 'assistant' as const,
        content: m.content,
      }))

      const res = await customGPTChatStream(id, content, history, sessionId, file, chatModel)
      if (!res.body) throw new Error('No response body')

      const reader = res.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let botResp = ''
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const { frames, rest } = takeNextStreamFrames(buffer)
        buffer = rest
        for (const frame of frames) {
          const parsed = applyStreamFrame(frame)
          if (parsed.kind === 'ignore') continue
          if (parsed.kind === 'error') throw new Error(parsed.error)
          botResp += parsed.text
          const partial: Message = { id: 'streaming', content: botResp, sender: 'aira', timestamp: new Date(), isStreaming: true }
          setSessions(prev => prev.map(s => {
            if (s.id !== sessionId) return s
            const without = s.messages.filter(m => m.id !== 'streaming')
            return { ...s, messages: [...without, partial], lastMessageAt: new Date(), messagesLoaded: true }
          }))
        }
      }

      // Drain any remaining complete frames (either delimiter).
      while (true) {
        const { frames, rest } = takeNextStreamFrames(buffer)
        buffer = rest
        if (frames.length === 0) break
        for (const frame of frames) {
          const parsed = applyStreamFrame(frame)
          if (parsed.kind === 'ignore') continue
          if (parsed.kind === 'error') throw new Error(parsed.error)
          botResp += parsed.text
        }
      }

      const final: Message = { id: (Date.now() + 1).toString(), content: botResp, sender: 'aira', timestamp: new Date() }
      setSessions(prev => prev.map(s => {
        if (s.id !== sessionId) return s
        const without = s.messages.filter(m => m.id !== 'streaming')
        return { ...s, messages: [...without, final], lastMessageAt: new Date(), messagesLoaded: true }
      }))
    } catch (e) {
      console.error('Playbook streaming error', e)
      toast.error('Failed to send message')
    } finally {
      setIsTyping(false)
      setSessions(prev => prev.map(s => {
        if (s.id !== sessionId) return s
        // If we failed before finalizing, clear the placeholder.
        return { ...s, messages: s.messages.filter(m => m.id !== 'streaming') }
      }))
    }
  }, [id, currentSessionId, createNewSession, sessions, chatModel])

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    if (!id) return
    if (!currentSessionId) return

    const session = sessions.find(s => s.id === currentSessionId)
    const originalMessages = session?.messages ?? []
    const visible = originalMessages.filter(m => m.id !== 'streaming' && m.is_deleted !== 'true')
    const editedIndex = visible.findIndex(m => m.id === messageId)
    if (editedIndex < 0) return

    // Remove the edited user message and everything after it.
    const trimmed = visible.slice(0, editedIndex)

    // Add new user message
    const newUserMsg: Message = { id: Date.now().toString(), content: newContent, sender: 'user', timestamp: new Date(), is_edited: 'true' }

    setSessions(prev => prev.map(s => {
      if (s.id !== currentSessionId) return s
      return { ...s, messages: [...trimmed, newUserMsg], messagesLoaded: true, lastMessageAt: new Date() }
    }))

    setIsTyping(true)
    abortControllerRef.current = new AbortController()
    // Show typing indicator immediately (before first stream chunk).
    const streamingPlaceholder: Message = { id: 'streaming', content: '', sender: 'aira', timestamp: new Date(), isStreaming: true }
    setSessions(prev => prev.map(s => {
      if (s.id !== currentSessionId) return s
      const without = s.messages.filter(m => m.id !== 'streaming')
      return { ...s, messages: [...without, streamingPlaceholder], lastMessageAt: new Date(), messagesLoaded: true }
    }))

    try {
      const history = trimmed.map(m => ({
        role: m.sender === 'user' ? 'user' as const : 'assistant' as const,
        content: m.content,
      }))

      const res = await editCustomGPTMessage(id, currentSessionId, messageId, newContent, history, chatModel)
      if (!res.body) throw new Error('No response body')

      const reader = res.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let botResp = ''
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const { frames, rest } = takeNextStreamFrames(buffer)
        buffer = rest
        for (const frame of frames) {
          const parsed = applyStreamFrame(frame)
          if (parsed.kind === 'ignore') continue
          if (parsed.kind === 'error') throw new Error(parsed.error)
          botResp += parsed.text
          const partial: Message = { id: 'streaming', content: botResp, sender: 'aira', timestamp: new Date(), isStreaming: true }
          setSessions(prev => prev.map(s => {
            if (s.id !== currentSessionId) return s
            const without = s.messages.filter(m => m.id !== 'streaming')
            return { ...s, messages: [...without, partial], lastMessageAt: new Date(), messagesLoaded: true }
          }))
        }
      }

      while (true) {
        const { frames, rest } = takeNextStreamFrames(buffer)
        buffer = rest
        if (frames.length === 0) break
        for (const frame of frames) {
          const parsed = applyStreamFrame(frame)
          if (parsed.kind === 'ignore') continue
          if (parsed.kind === 'error') throw new Error(parsed.error)
          botResp += parsed.text
        }
      }

      const final: Message = { id: (Date.now() + 1).toString(), content: botResp, sender: 'aira', timestamp: new Date() }
      setSessions(prev => prev.map(s => {
        if (s.id !== currentSessionId) return s
        const without = s.messages.filter(m => m.id !== 'streaming')
        return { ...s, messages: [...without, final], lastMessageAt: new Date(), messagesLoaded: true }
      }))
    } catch (e) {
      console.error('Playbook edit streaming error', e)
      toast.error('Failed to edit message')
      setSessions(prev => prev.map(s => (s.id === currentSessionId ? { ...s, messages: originalMessages, messagesLoaded: true } : s)))
    } finally {
      setIsTyping(false)
      setSessions(prev => prev.map(s => {
        if (s.id !== currentSessionId) return s
        return { ...s, messages: s.messages.filter(m => m.id !== 'streaming') }
      }))
    }
  }, [currentSessionId, id, sessions, chatModel])

  const title = playbook?.name ?? 'Playbook'
  const subtitle = playbook?.description?.trim() || 'Playbook chat'
  const apiBase = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`
  const displayName = user?.displayName ?? credentialUser?.username ?? 'User'

  const handleDeleteSession = useCallback((chatId: string) => {
    setChatToDelete(chatId)
    setDeleteDialogOpen(true)
  }, [])

  const confirmDeleteSession = useCallback(async () => {
    if (!chatToDelete) return
    try {
      await fetch(`${apiBase}chat/sessions/${chatToDelete}`, { method: 'DELETE', credentials: 'include' })
    } catch (e) {
      console.error('Failed to delete playbook chat session', e)
    }

    setSessions(prev => {
      const filtered = prev.filter(s => s.id !== chatToDelete)
      if (currentSessionId === chatToDelete) {
        setCurrentSessionId(filtered[0]?.id ?? null)
      }
      return filtered
    })

    setDeleteDialogOpen(false)
    setChatToDelete(null)
  }, [apiBase, chatToDelete, currentSessionId])

  const handleRenameSession = useCallback((chatId: string) => {
    setChatToRename(chatId)
    setRenameDialogOpen(true)
  }, [])

  const confirmRenameSession = useCallback(async (newTitle: string) => {
    if (!chatToRename) return
    try {
      const res = await fetch(`${apiBase}chat/sessions/${chatToRename}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTitle }),
        credentials: 'include',
      })
      if (!res.ok) throw new Error('rename failed')
      setSessions(prev => prev.map(s => (s.id === chatToRename ? { ...s, name: newTitle } : s)))
    } catch (e) {
      console.error('Failed to rename playbook chat session', e)
      toast.error('Failed to rename chat')
    }
    setRenameDialogOpen(false)
    setChatToRename(null)
  }, [apiBase, chatToRename])

  return (
    <div className="pbchat-shell">
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete chat?"
        description="This will remove the chat from your list. You can’t undo this action."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDeleteSession}
      />
      <RenameDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        currentTitle={sessions.find(s => s.id === chatToRename)?.name ?? ''}
        onConfirm={confirmRenameSession}
      />

      <div className="pbchat-topbar">
        <div className="pbchat-topbar-left">
          <button
            type="button"
            className="pbchat-back"
            onClick={() => navigate('/chat', { state: { initialView: 'playbooks' } })}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M8.8 3L5 7l3.8 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
          <div className="pbchat-titlewrap">
            <div className="pbchat-title">{title}</div>
            <div className="pbchat-subtitle">{subtitle}</div>
          </div>
        </div>
      </div>

      <div className="pbchat-main">
        <aside className="pbchat-sidebar" aria-label="Playbook chat sessions">
          <div className="pbchat-sidebar-head">
            <div className="pbchat-side-title">Sessions</div>
          </div>
          <div className="pbchat-sidebar-new">
            <button className="new-btn" onClick={startNewChatDraft} aria-label="Start new chat">
              <div className="new-ico" aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M5 1v8M1 5h8" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <span>New Chat</span>
            </button>
          </div>
          <div className="pbchat-sessionlist">
            {sessions.length === 0 ? (
              <div className="pbchat-session-meta">No sessions yet</div>
            ) : sessions.map(s => (
              <div key={s.id} className={`pbchat-session-row${s.id === currentSessionId ? ' active' : ''}`}>
                <button
                  type="button"
                  className="pbchat-session"
                  onClick={() => selectSession(s.id)}
                  aria-label={`Open session: ${s.name || 'Untitled'}`}
                >
                  <div className="pbchat-session-title">{s.name || 'Untitled'}</div>
                  <div className="pbchat-session-meta">{formatChatMeta(s)}</div>
                </button>
                <div className="pbchat-session-actions">
                  <button
                    type="button"
                    className="pbchat-session-action"
                    onClick={e => { e.stopPropagation(); handleRenameSession(s.id) }}
                    aria-label={`Rename session: ${s.name || 'Untitled'}`}
                    title="Rename"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M8.9 2.1l3 3M2 12h3l7.6-7.6a1 1 0 000-1.4l-1.6-1.6a1 1 0 00-1.4 0L2 9v3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="pbchat-session-action danger"
                    onClick={e => { e.stopPropagation(); handleDeleteSession(s.id) }}
                    aria-label={`Delete session: ${s.name || 'Untitled'}`}
                    title="Delete"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M3 4.5h8M5.2 4.5v7m3.6-7v7M5 2.8h4l.6.7H4.4l.6-.7zM4.2 4.5l.4 8a1 1 0 001 1h2.8a1 1 0 001-1l.4-8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <div className="pbchat-content">
          {loading ? (
            <div className="pbchat-empty">Loading playbook…</div>
          ) : (
            <>
              <ChatMessages
                messages={messages}
                isTyping={isTyping}
                messagesEndRef={messagesEndRef}
                onRegenerate={() => toast.info('Regenerate not yet supported for playbooks')}
                onEditMessage={editMessage}
                userName={displayName.split(' ')[0]}
                welcomeChips={playbook?.conversation_starters ?? []}
                onChipClick={text => void sendMessage(text)}
              />
              <InputBar
                onSendMessage={sendMessage}
                disabled={false}
                isStreaming={isTyping}
                onStop={stopStreaming}
                onToast={msg => toast(msg)}
                chatModel={chatModel}
                onChatModelChange={setChatModel}
                modelLocked={modelLocked}
                chatModelOptions={playbookChatModelOptions}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

